import { DragEvent, ReactNode, useMemo, useState } from 'react';
import { GripVertical, Plus, X } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { cn } from '@/frontend/lib/utils';
import { CELL_INPUT_KINDS, CellInput, InputSheet, InputValues, SweepCondition, SweepInput, emptyInputs, findInput, sweepGrid } from '@/reports/inputs';
import {
  Shelf,
  addCondition,
  runnerCandidates,
  conditionDefaults,
  conditionKind,
  linkCondition,
  locate,
  newCellInput,
  relabelCellInput,
  relabelCondition,
  removeCondition,
  removeInput,
  setConditionByRunner,
  setConditionDefaults,
  setConditionId,
  setConditionInBundle,
  shelveCondition,
  sweepSignature,
  sweepsReading,
  updateInput,
} from '@/reports/bundles/sheetEdit';
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../ui/alert-dialog';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import { Input } from '../../ui/input';
import { ChipsControl, Segmented } from '../../reportsV2/fields';
import { FillContextProvider } from '../../reportsV2/fillContext';
import { SweepControl } from '../../reportsV2/SweepControl';
import { IdField, KIND_ICONS, Switch, kindLabel } from './builderControls';

const SHELVES: Shelf[] = ['tabs', 'rows', 'columns'];
const SWATCHES = ['bg-sky-700', 'bg-orange-500', 'bg-green-600', 'bg-violet-600', 'bg-rose-500', 'bg-amber-500'];
const DRAG_CONDITION = 'application/x-veriform-condition';
const TRY = { editable: true, upload: async () => null, traces: {} };

/**
 * Building a sweep: what changes (each condition, its values, and whether the
 * bundle fixes them or the report runner sets them), what is recorded
 * in each cell, and how the fill page lays it out — with the fill page's own
 * table, live, to try it in. Works on a copy of the sheet; Save puts it back,
 * and closing with changes unsaved asks first.
 */
export function SweepBuilder({ sheet: original, sweepId, onSave, onCancel }: { sheet: InputSheet; sweepId: string; onSave: (sheet: InputSheet) => void; onCancel: () => void }) {
  const [sheet, setSheet] = useState(original);
  const sweep = findInput(sheet, sweepId) as SweepInput | undefined;
  const [tried, setTried] = useState<InputValues>({});
  const [dragging, setDragging] = useState<Shelf | null>(null);
  /** the condition or recorded input being named for the first time, whose id follows its name until the name box is left */
  const [naming, setNaming] = useState<{ kind: 'condition' | 'record'; index: number } | null>(null);
  const isNaming = (kind: 'condition' | 'record', index: number) => naming?.kind === kind && naming.index === index;
  /** conditions switched to the report runner that no input holds yet: their values stay in the bundle until one is made or picked */
  const [unlinked, setUnlinked] = useState<string[]>([]);
  const [confirmClose, setConfirmClose] = useState(false);
  const dirty = useMemo(() => JSON.stringify(sheet) !== JSON.stringify(original), [sheet, original]);
  const close = () => (dirty ? setConfirmClose(true) : onCancel());
  const tryInputs = useMemo(() => ({ ...emptyInputs(sheet), ...tried }), [sheet, tried]);
  if (!sweep) return null;

  const setSweep = (next: SweepInput) => setSheet((s) => updateInput(s, sweepId, () => next));
  const swatch = (id: string) => SWATCHES[sweep.conditions.findIndex((c) => c.id === id) % SWATCHES.length];
  const grid = sweepGrid(sheet, sweep, tryInputs);

  const dropOn = (shelf: Shelf) => (e: DragEvent) => {
    e.preventDefault();
    setDragging(null);
    const id = e.dataTransfer.getData(DRAG_CONDITION);
    if (id) setSweep(shelveCondition(sweep, id, shelf));
  };

  const deleteCondition = (id: string) => {
    const runnerInput = sweep.conditions.find((c) => c.id === id)?.input;
    let next = updateInput(sheet, sweepId, () => removeCondition(sweep, id));
    if (runnerInput && sweepsReading(next, runnerInput).length === 0) next = removeInput(next, runnerInput);
    setSheet(next);
    setNaming(null);
    setUnlinked((u) => u.filter((x) => x !== id));
  };

  const setValuesAt = (id: string, at: 'runner' | 'bundle') => {
    const condition = sweep.conditions.find((c) => c.id === id);
    if (at === 'runner') {
      if (!condition?.input) setUnlinked((u) => (u.includes(id) ? u : [...u, id]));
    } else {
      setUnlinked((u) => u.filter((x) => x !== id));
      if (condition?.input) setSheet((s) => setConditionInBundle(s, sweepId, id));
    }
  };
  const runnerFrom = (id: string, inputId: string | null) => {
    setSheet((s) => (inputId ? linkCondition(s, sweepId, id, inputId) : setConditionByRunner(s, sweepId, id)));
    setUnlinked((u) => u.filter((x) => x !== id));
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) close(); }}>
      <DialogContent data-slot="report-studio" className="flex max-h-[92svh] flex-col gap-0 overflow-hidden p-0 sm:max-w-[min(92rem,96vw)]">
        <DialogHeader className="flex-row items-center justify-between gap-[1rem] border-b-[1px] px-[1.25rem] py-[0.75rem]">
          <div>
            <p className="text-xs text-muted-foreground">{I18n.get('rv2SweepCrumb')}</p>
            <DialogTitle className="text-lg">{I18n.get('rv2SweepTitle', { label: sweep.label })}</DialogTitle>
            <DialogDescription className="sr-only">{I18n.get('rv2SweepHelp')}</DialogDescription>
          </div>
          <div className="mr-8 flex gap-2">
            <Button variant="outline" onClick={close}>{I18n.get('cancel')}</Button>
            <Button onClick={() => onSave(sheet)}>{I18n.get('rv2SaveSweep')}</Button>
          </div>
        </DialogHeader>
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-[1.25rem] overflow-y-auto bg-muted/40 p-[1.25rem] lg:grid-cols-[24rem_minmax(0,1fr)]">
          {/* 1 and 2: what changes, what is recorded */}
          <div className="flex flex-col gap-[1rem] rounded-xl border-[1px] bg-background p-[1rem]">
            <h3 className="flex items-center gap-2 text-base font-semibold"><Step n={1} /> {I18n.get('rv2WhatYouChange')}</h3>
            {sweep.conditions.map((c, i) => {
              const values = conditionDefaults(sheet, c);
              const runnerInput = c.input ? findInput(sheet, c.input) : undefined;
              const kind = conditionKind(sheet, c);
              // a condition set by the report runner keeps its unit on the runner input, where the runner sees it
              const unit = c.unit ?? (runnerInput?.kind === 'numbers' ? runnerInput.unit : undefined);
              const runnerAt = runnerInput ? locate(sheet, runnerInput.id) : null;
              const setUnit = (next: string) => (runnerInput?.kind === 'numbers'
                ? setSheet((s) => updateInput(s, runnerInput.id, (i) => ({ ...i, unit: next || undefined }) as typeof i))
                : setSweep({ ...sweep, conditions: sweep.conditions.map((x) => (x.id === c.id ? { ...x, unit: next || undefined } : x)) }));
              return (
                // keyed by position: the id follows the name as it is typed, and the name box must keep its focus
                <div key={i} className="flex flex-col gap-2 rounded-lg border-[1px] p-[0.75rem]">
                  <div className="flex items-center gap-2">
                    <span className={cn('size-2.5 shrink-0 rounded-[2px]', swatch(c.id))} />
                    <Input aria-label={I18n.get('rv2ConditionName')} className="h-7 font-medium" value={c.label}
                      onChange={(e) => setSweep(relabelCondition(sweep, c.id, e.target.value, isNaming('condition', i)))} onBlur={() => { if (isNaming('condition', i)) setNaming(null); }} />
                    <span className="shrink-0 text-xs text-muted-foreground">{values.length}{unit ? ` · ${unit}` : ''}</span>
                    <button type="button" aria-label={I18n.get('rv2RemoveItem', { item: c.label })} className="flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted" onClick={() => deleteCondition(c.id)}><X className="size-3.5" /></button>
                  </div>
                  <IdRow>
                    <IdField compact label={I18n.get('rv2ConditionId', { condition: c.label || c.id })} value={c.id} takenMessage={I18n.get('rv2IdTakenInSweep')}
                      taken={(id) => sweep.conditions.some((x) => x.id === id)} onCommit={(id) => { setSweep(setConditionId(sweep, c.id, id)); setUnlinked((u) => u.map((x) => (x === c.id ? id : x))); }} />
                  </IdRow>
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <ChipsControl input={kind === 'numbers' ? { id: c.id, kind: 'numbers', label: c.label, unit } : { id: c.id, kind: 'names', label: c.label }} value={values} onChange={(v) => setSheet((s) => setConditionDefaults(s, sweepId, c.id, v))} />
                    </div>
                    {kind === 'numbers' && <Input aria-label={I18n.get('rv2ConditionUnit', { condition: c.label })} className="h-9 w-16" placeholder={I18n.get('rv2Unit')} value={unit ?? ''} onChange={(e) => setUnit(e.target.value)} />}
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-muted-foreground">{I18n.get('rv2ValuesSet')}</span>
                    <Segmented size="sm" value={c.input || unlinked.includes(c.id) ? 'runner' : 'bundle'}
                      onChange={(v) => { if (v === 'runner' || v === 'bundle') setValuesAt(c.id, v); }}
                      options={[{ value: 'bundle', label: I18n.get('rv2InBundle') }, { value: 'runner', label: I18n.get('rv2ByTheRunner') }]} />
                  </div>
                  {!c.input && unlinked.includes(c.id) && <RunnerInputPrompt sheet={sheet} condition={c} onPick={(inputId) => runnerFrom(c.id, inputId)} />}
                  {(runnerInput?.kind === 'names' || runnerInput?.kind === 'numbers') && (
                    <>
                      <label className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                        {I18n.get('rv2RunnerInput')}
                        <select aria-label={I18n.get('rv2RunnerInputFor', { condition: c.label || c.id })} value={runnerInput.id} onChange={(e) => setSheet((s) => linkCondition(s, sweepId, c.id, e.target.value))}
                          className="h-7 max-w-[14rem] min-w-0 rounded-md border-[1px] border-input bg-background px-1.5 text-xs text-foreground">
                          {runnerCandidates(sheet, c).map((i) => {
                            const where = locate(sheet, i.id);
                            return <option key={i.id} value={i.id}>{`${i.label || i.id}${where ? ` · ${sheet.sections[where.section].title}` : ''}`}</option>;
                          })}
                        </select>
                      </label>
                      <Switch label={I18n.get('rv2OnlyDefaults')} help={I18n.get('rv2OnlyDefaultsHelp')} checked={!!runnerInput.onlyDefaults}
                        onChange={(only) => setSheet((s) => updateInput(s, runnerInput.id, (i) => ({ ...i, onlyDefaults: only || undefined }) as typeof i))} />
                      <p className="text-xs text-muted-foreground">{I18n.get(runnerInput.onlyDefaults ? 'rv2RunnerPicks' : 'rv2RunnerDefaults', { section: runnerAt ? sheet.sections[runnerAt.section].title : '' })}</p>
                    </>
                  )}
                </div>
              );
            })}
            <DropdownMenu>
              <DropdownMenuTrigger render={<Button variant="outline" className="border-dashed" />}><Plus /> {I18n.get('rv2AddCondition')}</DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={() => { setSweep(addCondition(sweep, 'names')); setNaming({ kind: 'condition', index: sweep.conditions.length }); }}>{I18n.get('rv2ConditionNames')}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => { setSweep(addCondition(sweep, 'numbers')); setNaming({ kind: 'condition', index: sweep.conditions.length }); }}>{I18n.get('rv2ConditionNumbers')}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <h3 className="mt-2 flex items-center gap-2 border-t-[1px] pt-[1rem] text-base font-semibold"><Step n={2} /> {I18n.get('rv2RecordInEachCell')}</h3>
            {sweep.record.map((r, i) => <CellInputRow key={i} input={r} onChange={(next) => setSweep({ ...sweep, record: sweep.record.map((x, j) => (j === i ? next : x)) })}
              onRelabel={(label) => setSweep(relabelCellInput(sweep, i, label, isNaming('record', i)))} onNamed={() => { if (isNaming('record', i)) setNaming(null); }} taken={(id) => sweep.record.some((x) => x.id === id)} onRemove={sweep.record.length > 1 ? () => { setSweep({ ...sweep, record: sweep.record.filter((_, j) => j !== i) }); setNaming(null); } : undefined} />)}
            <DropdownMenu>
              <DropdownMenuTrigger render={<Button variant="outline" className="border-dashed" />}><Plus /> {I18n.get('rv2AddCellInput')}</DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {CELL_INPUT_KINDS.map((k) => {
                  const Icon = KIND_ICONS[k];
                  return <DropdownMenuItem key={k} onClick={() => { setSweep({ ...sweep, record: [...sweep.record, newCellInput(sweep, k as CellInput['kind'])] }); setNaming({ kind: 'record', index: sweep.record.length }); }}><Icon /> {kindLabel(k)}</DropdownMenuItem>;
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* 3: lay it out */}
          <div className="flex min-w-0 flex-col gap-[1rem] rounded-xl border-[1px] bg-background p-[1rem]">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="flex items-center gap-2 text-base font-semibold"><Step n={3} /> {I18n.get('rv2LayItOut')}</h3>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{I18n.get('rv2DragBetweenShelves')}</span>
                <Segmented size="sm" value={sweep.layout.view ?? 'auto'} onChange={(v) => setSweep({ ...sweep, layout: { ...sweep.layout, view: v === 'auto' || v === null ? undefined : v } })}
                  options={[{ value: 'auto', label: I18n.get('rv2Automatic') }, { value: 'grid', label: I18n.get('rv2Grid') }, { value: 'flat', label: I18n.get('rv2FlatTable') }]} />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              {SHELVES.map((shelf) => (
                <div key={shelf} onDragOver={(e) => { if (e.dataTransfer.types.includes(DRAG_CONDITION)) { e.preventDefault(); setDragging(shelf); } }} onDragLeave={() => setDragging(null)} onDrop={dropOn(shelf)}
                  className={cn('flex min-h-11 items-center gap-[0.75rem] rounded-lg border-[1px] border-dashed px-[0.75rem] py-1.5', dragging === shelf && 'border-solid border-sky-700 bg-sky-50')}>
                  <span className="w-16 shrink-0 text-xs font-medium text-muted-foreground">{I18n.get(`rv2Shelf_${shelf}`)}</span>
                  <div className="flex flex-wrap gap-1.5">
                    {sweep.layout[shelf].map((id) => {
                      const c = sweep.conditions.find((x) => x.id === id);
                      if (!c) return null;
                      return (
                        <DropdownMenu key={id}>
                          <DropdownMenuTrigger render={<button type="button" draggable onDragStart={(e) => e.dataTransfer.setData(DRAG_CONDITION, id)}
                            className="flex h-7 cursor-grab items-center gap-1.5 rounded-md border-[1px] bg-background px-2 text-sm shadow-xs" />}>
                            <GripVertical className="size-3 text-muted-foreground" /><span className={cn('size-2.5 rounded-[2px]', swatch(id))} />{c.label || c.id}
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start">
                            {SHELVES.filter((s) => s !== shelf).map((s) => <DropdownMenuItem key={s} onClick={() => setSweep(shelveCondition(sweep, id, s))}>{I18n.get('rv2MoveTo', { shelf: I18n.get(`rv2Shelf_${s}`) })}</DropdownMenuItem>)}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      );
                    })}
                  </div>
                  {shelf === 'tabs' && sweep.layout.tabs.length > 0 && <span className="ml-auto text-xs text-muted-foreground">{I18n.get('rv2TabsHelp')}</span>}
                </div>
              ))}
            </div>
            <FillContextProvider value={TRY}>
              <SweepControl key={JSON.stringify(sweep.layout)} sheet={sheet} sweep={sweep} inputs={tryInputs} onChange={(v) => setTried((t) => ({ ...t, [sweep.id]: v }))} />
            </FillContextProvider>
            <div className="grid gap-[0.75rem] md:grid-cols-2">
              <div className="rounded-lg border-[1px] p-[0.75rem]">
                <p className="font-medium">{I18n.get('rv2CellsValues', { cells: String(grid.cells.length), values: String(grid.cells.length * sweep.record.length) })}</p>
                <p className="text-xs text-muted-foreground">{grid.conditions.map((c) => `${c.values.length} ${(c.label || c.id).toLowerCase()}`).join(' × ') || '—'}</p>
              </div>
              <div className="rounded-lg border-[1px] p-[0.75rem]">
                <p className="font-medium">{I18n.get('rv2ReportReceives')}</p>
                <pre className="mt-1 overflow-x-auto rounded bg-muted px-2 py-1 font-mono text-xs whitespace-pre-wrap">{sweepSignature(sweep)}</pre>
                <p className="mt-1 text-xs text-muted-foreground">{I18n.get('rv2PresentationOnly')}</p>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
      <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{I18n.get('rv2UnsavedSweepTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{I18n.get('rv2UnsavedSweep', { label: sweep.label || sweep.id })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{I18n.get('rv2KeepEditing')}</AlertDialogCancel>
            <Button variant="destructive" onClick={onCancel}>{I18n.get('rv2Discard')}</Button>
            <Button onClick={() => onSave(sheet)}>{I18n.get('rv2SaveSweep')}</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}

/** A condition just switched to the report runner: make an input to hold its values, or pick one already on the sheet. */
function RunnerInputPrompt({ sheet, condition, onPick }: { sheet: InputSheet; condition: SweepCondition; onPick: (inputId: string | null) => void }) {
  const candidates = runnerCandidates(sheet, condition);
  return (
    <div className="flex flex-col gap-2 rounded-md border-[1px] border-dashed p-2">
      <p className="text-xs text-muted-foreground">{I18n.get(candidates.length > 0 ? 'rv2NoRunnerInput' : 'rv2NoRunnerInputNone')}</p>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={() => onPick(null)}><Plus /> {I18n.get('rv2CreateRunnerInput')}</Button>
        {candidates.length > 0 && (
          <select aria-label={I18n.get('rv2RunnerInputFor', { condition: condition.label || condition.id })} value="" onChange={(e) => { if (e.target.value) onPick(e.target.value); }}
            className="h-7 max-w-[14rem] min-w-0 rounded-md border-[1px] border-input bg-background px-1.5 text-xs text-foreground">
            <option value="" disabled>{I18n.get('rv2PickRunnerInput')}</option>
            {candidates.map((i) => {
              const where = locate(sheet, i.id);
              return <option key={i.id} value={i.id}>{`${i.label || i.id}${where ? ` · ${sheet.sections[where.section].title}` : ''}`}</option>;
            })}
          </select>
        )}
      </div>
    </div>
  );
}

function Step({ n }: { n: number }) {
  return <span className="flex size-5 items-center justify-center rounded-full bg-foreground text-xs text-background">{n}</span>;
}

/** An identifier under a condition or recorded input: what the report's code reads it by. */
function IdRow({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-start gap-2">
      <span className="mt-1 shrink-0 text-xs text-muted-foreground">{I18n.get('rv2Identifier')}</span>
      {children}
    </div>
  );
}

/** One input recorded in each cell: its question, kind, unit and identifier. */
function CellInputRow({ input, onChange, onRelabel, onNamed, taken, onRemove }: {
  input: CellInput;
  onChange: (input: CellInput) => void;
  onRelabel: (label: string) => void;
  /** the name box left: a new input's id stops following its name */
  onNamed: () => void;
  taken: (id: string) => boolean;
  onRemove?: () => void;
}) {
  return (
    <div className="flex flex-col gap-1.5 rounded-lg border-[1px] px-2 py-1.5">
      <div className="flex items-center gap-1.5">
        <Input aria-label={I18n.get('rv2CellInputName')} className="h-7 flex-1" value={input.label} onChange={(e) => onRelabel(e.target.value)} onBlur={onNamed} />
        <span className="shrink-0 text-xs text-muted-foreground">{kindLabel(input.kind)}</span>
        {input.kind === 'number' && <Input aria-label={I18n.get('rv2Unit')} className="h-7 w-14" placeholder={I18n.get('rv2Unit')} value={input.unit ?? ''} onChange={(e) => onChange({ ...input, unit: e.target.value })} />}
        {onRemove && <button type="button" aria-label={I18n.get('rv2RemoveItem', { item: input.label })} className="flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted" onClick={onRemove}><X className="size-3.5" /></button>}
      </div>
      <IdRow>
        <IdField compact label={I18n.get('rv2CellInputId', { input: input.label || input.id })} value={input.id} takenMessage={I18n.get('rv2IdTakenInSweep')} taken={taken} onCommit={(id) => onChange({ ...input, id })} />
      </IdRow>
    </div>
  );
}
